import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Award, ChevronLeft, Lock, Camera } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api/client';
import { getBadgeIcon, CATEGORY_ICONS } from '@/utils/badgeIcons';
import { catalogText } from '@/utils/catalog';

interface Badge {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  rarity: string;
  category: string;
  criteriaType: string;
  criteriaThreshold: number;
  xpReward: number;
  isSecret: boolean;
  earned: boolean;
  earnedAt: string | null;
}

const RARITY_COLORS: Record<string, { bg: string; text: string; border: string; ring: string }> = {
  COMMON: { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-warm-border', ring: 'ring-steel/30' },
  UNCOMMON: { bg: 'bg-forest-wash', text: 'text-forest', border: 'border-forest/20', ring: 'ring-forest/30' },
  RARE: { bg: 'bg-steel/10', text: 'text-steel', border: 'border-steel/20', ring: 'ring-steel/30' },
  EPIC: { bg: 'bg-violet/10', text: 'text-violet', border: 'border-violet/20', ring: 'ring-violet/30' },
  LEGENDARY: { bg: 'bg-gold/10', text: 'text-gold', border: 'border-gold/20', ring: 'ring-gold/30' },
};

export default function BadgesPage() {
  const { t } = useTranslation('scout');
  const navigate = useNavigate();
  const { isAuthenticated, scout } = useAuthStore();
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    loadBadges();
  }, [isAuthenticated, navigate]);

  async function loadBadges() {
    try {
      setLoadError(false);
      const data = await api.get<Badge[]>('/scout/badges');
      setBadges(data);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }

  // Group badges by category
  const categories = Array.from(new Set(badges.map((b) => b.category)));
  const filteredBadges = selectedCategory ? badges.filter((b) => b.category === selectedCategory) : badges;

  const earnedCount = badges.filter((b) => b.earned).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-violet/5 to-white flex flex-col items-center justify-center px-6">
        <Award className="w-12 h-12 text-steel mb-3" />
        <p className="text-small text-muted-foreground text-center mb-4">{t('badges.loadError')}</p>
        <button
          onClick={() => {
            setLoading(true);
            loadBadges();
          }}
          className="px-5 py-2.5 rounded-xl bg-muted text-small font-medium text-foreground"
        >
          {t('badges.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet/5 to-white pb-24">
      {/* Header */}
      <div className="px-4 pt-6 pb-4 flex items-center gap-3">
        <button onClick={() => navigate('/')} className="p-2 -ms-2 rounded-lg hover:bg-warm-faint">
          <ChevronLeft className="w-5 h-5 text-muted-foreground" />
        </button>
        <div>
          <h1 className="text-section font-bold text-foreground">{t('badges.title')}</h1>
          <p className="text-small text-muted-foreground">
            {t('badges.progress', { earned: earnedCount, total: badges.length })}
          </p>
        </div>
      </div>

      {/* Category filters */}
      <div className="px-4 mb-4 flex gap-2 overflow-x-auto pb-1">
        <button
          onClick={() => setSelectedCategory(null)}
          className={`px-3 py-2 rounded-full text-caption font-medium whitespace-nowrap transition-colors ${
            !selectedCategory ? 'bg-foreground text-white' : 'bg-muted text-muted-foreground hover:bg-warm-faint'
          }`}
        >
          {t('badges.all')}
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={`px-3 py-2 rounded-full text-caption font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${
              selectedCategory === cat
                ? 'bg-foreground text-white'
                : 'bg-muted text-muted-foreground hover:bg-warm-faint'
            }`}
          >
            {(() => {
              const Icon = CATEGORY_ICONS[cat] || Award;
              return <Icon className="w-3.5 h-3.5" />;
            })()}
            {t(`badges.category.${cat}`)}
          </button>
        ))}
      </div>

      {/* Badge grid */}
      <div className="px-4 grid grid-cols-3 gap-3">
        {filteredBadges.map((badge) => (
          <BadgeCard key={badge.id} badge={badge} scout={scout} t={t} />
        ))}
      </div>

      {filteredBadges.length === 0 && (
        <div className="text-center py-12">
          {earnedCount > 0 ? (
            <>
              <Award className="w-12 h-12 text-gold mx-auto mb-3" />
              <p className="text-muted-foreground">{t('badges.allEarned')}</p>
            </>
          ) : (
            <>
              <Award className="w-12 h-12 text-steel mx-auto mb-3" />
              <p className="text-muted-foreground mb-4">{t('badges.empty')}</p>
              <button
                onClick={() => navigate('/scan')}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-primary to-forest text-white font-medium text-small active:scale-[0.98] transition-all"
              >
                <Camera className="w-4 h-4" />
                {t('common.openScanner')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Map badge criteria types to scout profile stats
function getProgress(
  criteriaType: string,
  scout: {
    totalScans: number;
    totalFeedback: number;
    currentStreak: number;
    longestStreak: number;
    totalIssues: number;
    totalReferrals: number;
    totalCompanyReferrals: number;
  } | null,
): number | null {
  if (!scout) return null;
  switch (criteriaType) {
    case 'TOTAL_SCANS':
      return scout.totalScans;
    case 'TOTAL_FEEDBACK':
      return scout.totalFeedback;
    case 'STREAK_DAYS':
      return Math.max(scout.currentStreak, scout.longestStreak);
    case 'TOTAL_ISSUES':
      return scout.totalIssues;
    case 'REFERRALS':
      return scout.totalReferrals;
    case 'COMPANY_REFERRALS':
      return scout.totalCompanyReferrals;
    default:
      return null;
  }
}

function BadgeCard({
  badge,
  scout,
  t,
}: {
  badge: Badge;
  scout: {
    totalScans: number;
    totalFeedback: number;
    currentStreak: number;
    longestStreak: number;
    totalIssues: number;
    totalReferrals: number;
    totalCompanyReferrals: number;
  } | null;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const colors = RARITY_COLORS[badge.rarity] ?? RARITY_COLORS.COMMON!;
  const Icon = getBadgeIcon(badge.slug, badge.category);
  const isLegendary = badge.rarity === 'LEGENDARY';
  const isEpic = badge.rarity === 'EPIC';

  const progress = !badge.earned && !badge.isSecret ? getProgress(badge.criteriaType, scout) : null;
  const progressPct =
    progress !== null && badge.criteriaThreshold > 0 ? Math.min((progress / badge.criteriaThreshold) * 100, 100) : null;

  return (
    <div
      className={`relative p-3 rounded-xl border text-center transition-all ${
        badge.earned
          ? `${colors.bg} ${colors.border} shadow-sm ${isLegendary ? 'ring-1 ring-gold/40' : isEpic ? 'ring-1 ring-violet/30' : ''}`
          : 'bg-warm-faint border-warm-border opacity-60'
      }`}
    >
      {/* Icon */}
      <div
        className={`w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center ${
          badge.earned ? `${colors.bg} ring-2 ${colors.ring}` : 'bg-muted'
        }`}
      >
        {badge.earned ? (
          <Icon className={`w-6 h-6 ${colors.text}`} />
        ) : badge.isSecret ? (
          <span className="text-lg text-muted-foreground font-bold">?</span>
        ) : (
          <Lock className="w-5 h-5 text-steel" />
        )}
      </div>

      {/* Name */}
      <p className="text-caption font-semibold text-foreground leading-tight">
        {catalogText(badge.slug, 'name', badge.name)}
      </p>

      {/* Rarity */}
      <span className={`text-[10px] font-medium ${colors.text} mt-0.5 block`}>
        {t(`badges.rarity.${badge.rarity}`)}
      </span>

      {/* XP reward or progress */}
      {badge.earned ? (
        <span className="text-[10px] text-forest font-medium">+{badge.xpReward} XP</span>
      ) : progressPct !== null && progress !== null ? (
        <div className="mt-1">
          <div className="h-1 bg-muted rounded-full overflow-hidden mx-1">
            <div className="h-full bg-steel/40 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
          </div>
          <span className="text-[10px] text-muted-foreground">
            {progress}/{badge.criteriaThreshold}
          </span>
        </div>
      ) : null}
    </div>
  );
}
